/**
 * Admin review screen for customer-submitted "Add Student" requests
 * (OWZ-9 Round 4). Mirrors the HistorySubmissionReview UX so the admin
 * mental model carries over.
 *
 * Backs onto the Round 2 endpoints under /api/admin/student-add-submissions.
 * Approve flow lets the admin pick program_ids (multi-select from
 * GET /api/programs) before creating the real Student record; reject
 * flow requires reviewer_notes which surface back to the customer in
 * their portal (Round 3, AddStudentEmptyState rejected row).
 */
import { useState, useEffect, useCallback } from 'react';
import {
  UserPlus,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  AlertCircle,
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { useAdminAuth } from '../../hooks/useAuth';

import { PageTitle } from './shared';
import { useRememberedTab } from './navigation/useRememberedTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  pending: { className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300', label: 'Pending' },
  approved: { className: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300', label: 'Approved' },
  rejected: { className: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300', label: 'Rejected' },
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const formatDob = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
};

const SubmissionRow = ({ submission, onApprove, onReject, disabled }) => {
  const badge = STATUS_BADGE[submission.status] || STATUS_BADGE.pending;
  const dob = formatDob(submission.student_dob);
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <UserPlus className="h-4 w-4 text-red-600 dark:text-red-400" />
              <h3 className="font-semibold truncate">{submission.student_name}</h3>
              <Badge className={badge.className}>{badge.label}</Badge>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Submitted by{' '}
              <span className="font-medium">
                {submission.customer_name || 'Unknown Customer'}
              </span>
              {submission.customer_email && (
                <> &middot; {submission.customer_email}</>
              )}
              {submission.created_at && (
                <> &middot; {formatDate(submission.created_at)}</>
              )}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 space-x-3">
              {submission.student_email && (
                <span>Email: {submission.student_email}</span>
              )}
              {dob && <span>DOB: {dob}</span>}
            </div>
            {submission.notes && (
              <p className="text-sm italic text-gray-600 dark:text-gray-400 mt-2">
                "{submission.notes}"
              </p>
            )}
            {submission.status === 'rejected' && submission.reviewer_notes && (
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                <span className="font-medium">Reviewer notes:</span>{' '}
                {submission.reviewer_notes}
              </p>
            )}
          </div>
          {submission.status === 'pending' && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                onClick={() => onApprove(submission)}
                disabled={disabled}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onReject(submission)}
                disabled={disabled}
              >
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const REVIEW_TABS = ['pending', 'approved', 'rejected', 'all'];

const StudentSubmissionReview = () => {
  const { token } = useAdminAuth();

  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useRememberedTab(
    'addstudentreviews.status-tab', REVIEW_TABS, 'pending',
  );

  const [approveTarget, setApproveTarget] = useState(null);
  const [approveProgramIds, setApproveProgramIds] = useState([]);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [processing, setProcessing] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (activeTab !== 'all') params.set('status', activeTab);

      const [subsResponse, statsResponse] = await Promise.all([
        fetch(
          `${BACKEND_URL}/api/admin/student-add-submissions?${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
        fetch(
          `${BACKEND_URL}/api/admin/student-add-submissions/stats`,
          { headers: { Authorization: `Bearer ${token}` } }
        ),
      ]);

      if (!subsResponse.ok) throw new Error('Failed to load submissions');

      const subsData = await subsResponse.json();
      setSubmissions(Array.isArray(subsData) ? subsData : []);

      if (statsResponse.ok) {
        setStats(await statsResponse.json());
      }
    } catch {
      setError('Failed to load student-add submissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  const loadPrograms = useCallback(async () => {
    try {
      // Public endpoint — no auth header needed, kept simple to share the
      // same canonical list with the public site.
      const response = await fetch(`${BACKEND_URL}/api/programs`);
      if (response.ok) {
        const data = await response.json();
        setPrograms(Array.isArray(data) ? data : []);
      }
    } catch {
      // Approve flow falls back to an empty program list — admin can still
      // approve a student with zero programs and assign them later.
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  const openApprove = (submission) => {
    setApproveTarget(submission);
    setApproveProgramIds([]);
  };

  const toggleProgramId = (id) => {
    setApproveProgramIds((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );
  };

  const submitApprove = async () => {
    if (!approveTarget) return;
    setProcessing(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/student-add-submissions/${approveTarget.id}/approve`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ program_ids: approveProgramIds }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Failed to approve submission');
        return;
      }
      setApproveTarget(null);
      setApproveProgramIds([]);
      loadData();
    } catch {
      setError('An error occurred while approving');
    } finally {
      setProcessing(false);
    }
  };

  const openReject = (submission) => {
    setRejectTarget(submission);
    setRejectNotes('');
  };

  const submitReject = async () => {
    if (!rejectTarget || !rejectNotes.trim()) return;
    setProcessing(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/admin/student-add-submissions/${rejectTarget.id}/reject`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ reviewer_notes: rejectNotes.trim() }),
        }
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || 'Failed to reject submission');
        return;
      }
      setRejectTarget(null);
      setRejectNotes('');
      loadData();
    } catch {
      setError('An error occurred while rejecting');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageTitle
        title="Student Add Submissions"
        description="Review and approve parent-submitted requests to add a new student to their membership"
        icon={<UserPlus className="h-6 w-6" />}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending">
            <Clock className="w-4 h-4 mr-1" />
            Pending
            {stats.pending > 0 && (
              <Badge className="ml-2 bg-amber-500">{stats.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approved">
            <CheckCircle className="w-4 h-4 mr-1" />
            Approved
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <XCircle className="w-4 h-4 mr-1" />
            Rejected
          </TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : submissions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500 dark:text-gray-400">
                No submissions found
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => (
                <SubmissionRow
                  key={submission.id}
                  submission={submission}
                  onApprove={openApprove}
                  onReject={openReject}
                  disabled={processing}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Approve dialog (program multi-select) */}
      <Dialog
        open={!!approveTarget}
        onOpenChange={(open) => {
          if (!open) {
            setApproveTarget(null);
            setApproveProgramIds([]);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve student-add submission</DialogTitle>
            <DialogDescription>
              {approveTarget && (
                <>
                  Pick the programs <strong>{approveTarget.student_name}</strong>{' '}
                  should be enrolled in. A real student record will be created
                  on{' '}
                  <strong>
                    {approveTarget.customer_name || 'this customer'}
                  </strong>
                  's account.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
            {programs.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No programs are configured. The student will be approved with no
                program enrollments — you can add programs later from Student
                Management.
              </p>
            ) : (
              programs.map((program) => {
                const checked = approveProgramIds.includes(program.id);
                return (
                  <label
                    key={program.id}
                    className="flex items-start gap-2 p-2 rounded border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleProgramId(program.id)}
                      aria-label={`Enroll in ${program.title}`}
                      className="mt-1"
                    />
                    <div className="text-sm">
                      <p className="font-medium">{program.title}</p>
                      {program.age_range && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {program.age_range}
                        </p>
                      )}
                    </div>
                  </label>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setApproveTarget(null);
                setApproveProgramIds([]);
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              onClick={submitApprove}
              disabled={processing}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Approving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-1" /> Approve &amp; Create Student
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog (required notes) */}
      <Dialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRejectTarget(null);
            setRejectNotes('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject student-add submission</DialogTitle>
            <DialogDescription>
              Tell{' '}
              <strong>{rejectTarget?.customer_name || 'the customer'}</strong>{' '}
              why this submission can't be approved. They'll see your note in
              their portal.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="student-add-reject-notes">
              Reason <span className="text-red-600">*</span>
            </Label>
            <Textarea
              id="student-add-reject-notes"
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="e.g., This student already exists on another account. Please contact us."
              rows={4}
            />
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectTarget(null);
                setRejectNotes('');
              }}
              disabled={processing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={submitReject}
              disabled={processing || !rejectNotes.trim()}
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" /> Rejecting...
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 mr-1" /> Reject submission
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StudentSubmissionReview;
